import React from 'react';
import ProgressCard from '../../components/ProgressCard';
import JDOverview from '../../components/JDOverview';
import JDPostingPlan from '../../components/JDPostingPlan';
import JobPostingDetails from '../../components/JobPostingDetails';
import JobPostingLogs from '../../components/JobPostingLogs';
import TalentScoutingResults from '../../components/TalentScoutingResults';
import CandidateResults from '../../components/CandidateResults';
import OutreachContent from '../../components/OutreachContent';
import OutreachLogs from '../../components/OutreachLogs';
import ChatCard from '../../components/ChatCard';
import JobStatusPanel from '../../components/JobStatusPanel';
import ChatHeaderControls from '../../components/ChatHeaderControls';
import InterviewWorkspace from '../../components/InterviewScheduler/InterviewWorkspace';
import { canUseCompanyFeature, getFeatureBlockedMessage } from '../../utils/companyFeatureAccess';

const LightbulbIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
    />
  </svg>
);

const BarChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const ChatRightPanel = ({
  layout,
  header,
  jobPosting,
  sourcing,
  outreach,
  analysis,
  handlers,
  pipelineSnapshot,
  featureAccess,
}) => {
  const {
    isStackedPanels,
    mobilePanelView,
    isMobileLayout,
    centerWidth,
    rightPanelMinWidthPx,
    isDark,
    rightPanelTransitioning,
    primaryColor,
    secondaryColor,
    onMobilePanelViewChange,
  } = layout;

  const {
    activeHeaderTab,
    agentStageLabel,
    lastResponseType,
    onNavigateTab,
    onOpenPipelineFilter,
    fromDashboard,
    interviewRefreshKey,
    interviewSetupDraft,
  } = header;

  const {
    showJobPostingJdPanel,
    showJobPostingPlanPanel,
    jobTitle,
    jdText,
    finalRequirements,
    availablePostingChannels,
    sourcingComplete,
    talentScoutingStarted,
    jobPostingPhase,
    jobPostingProcessing,
    jobPostingComplete,
    jobPostingFailed,
    selectedChannels,
    selectedJobCandidateCount,
    selectedJobHasPostedJob,
    postingDetails,
    jobPostingLogs,
    linkedinLogs,
    naukriLogs,
    linkedinProcessing,
    naukriProcessing,
    shouldForceJobPostingPanel,
    postedChannels,
    syncingPostedApplicants,
    postedApplicantsLastSyncTime,
    jobPostingExpandToken,
  } = jobPosting;

  const {
    candidates,
    sourcingStats,
    automationSessionId,
    automationSearchId,
    panelSessionId,
    panelSearchId,
    talentPoolChannelFilter,
    googleCandidateCount,
    linkedinCandidateCount,
    googleSourcingLogs,
    linkedinSourcingLogs,
    resumeBoxSourcingLogs,
    sourcingLogs,
    googleSourcingProcessing,
    linkedinSourcingProcessing,
    resumeBoxSourcingProcessing,
    resumeBoxCandidateCount,
    googleSearchFoundCount,
    linkedinSearchFoundCount,
    enrichedCandidateCount,
    matchedCandidateCount,
    sourcingExpandToken,
    sourcingRefreshToken,
    selectedSourcingChannels,
    pipelineCandidateStatusFilter,
  } = sourcing;

  const {
    outreachPhase,
    rightPanelMode,
    outreachCompleted,
    outreachLogs,
    outreachSummary,
    outreachSending,
    outreachCandidateIds,
    outreachCandidates,
    selectedOutreachChannels,
    outreachTemplate,
    outreachWorkspaceName,
    outreachTemplatesByChannel,
    isLoadingOutreachTemplate,
    emailSyncState,
  } = outreach;

  const {
    showResultsPanel,
    analysisUxPhase,
    chatApiLoading,
    lifecycleLoading,
    isJDLoading,
  } = analysis;

  const {
    handleUserQueryForJD,
    getPostJobCommand,
    onPostingDetailsChange,
    onPostingDetailsTriggerMessage,
    onOpenJobPostingPreview,
    onRequestPostJob,
    onRetryPostChannel,
    onSyncApplicants,
    initiateOutreach,
    onViewCandidate,
    onCandidateRowClick,
    onChannelFilterChange,
    handleBackFromOutreach,
    handleSendOutreach,
    handleStartOutreach,
    onRefreshStatus,
    onStartInterviewCreation,
    onOpenScorecard,
  } = handlers;

  const canUseFeature = (feature) => canUseCompanyFeature(featureAccess, feature);
  const canUseNormalMailSync = canUseFeature('outreach') || canUseFeature('scheduling');
  const canUseInterviews = canUseFeature('manual_interview') || canUseFeature('ai_interview');
  const visibleTabs = [
    'status',
    ...(canUseFeature('job_posting') ? ['job_posting'] : []),
    ...(canUseFeature('sourcing') ? ['sourcing'] : []),
    ...(canUseInterviews ? ['interview'] : []),
    ...((jdText || analysisUxPhase === 'done') ? ['overview'] : []),
  ];
  const renderedHeaderTab = visibleTabs.includes(activeHeaderTab) ? activeHeaderTab : 'status';
  const blockedPanel = (feature) => (
    <ChatCard className="mt-2 p-5 text-center">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Access not enabled</h3>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{getFeatureBlockedMessage(feature)}</p>
    </ChatCard>
  );

  const candidateResultsProps = {
    candidates,
    sourcingStats,
    onStartOutreach: canUseFeature('outreach') ? initiateOutreach : null,
    sessionId: panelSessionId || automationSessionId,
    searchId: panelSearchId || automationSearchId,
    jdId: panelSessionId || automationSessionId,
    jobTitle,
    postedChannels,
    applicantSyncEnabled: canUseFeature('job_posting'),
    onSyncApplicants: canUseFeature('job_posting') ? onSyncApplicants : null,
    onSyncReplies: canUseNormalMailSync ? () => handleUserQueryForJD('sync replies') : null,
    onViewCandidate,
    onCandidateRowClick,
    channelFilter: talentPoolChannelFilter,
    onChannelFilterChange,
    googleCandidateCount,
    linkedinCandidateCount,
    googleLogs: googleSourcingLogs,
    linkedinLogs: linkedinSourcingLogs,
    resumeBoxLogs: resumeBoxSourcingLogs,
    googleProcessing: googleSourcingProcessing,
    linkedinProcessing: linkedinSourcingProcessing,
    resumeBoxProcessing: resumeBoxSourcingProcessing,
    resumeBoxCandidateCount,
    selectedSourcingChannels,
    showSourcingLogs: true,
    googleSearchFoundCount,
    linkedinSearchFoundCount,
    enrichedCandidateCount,
    matchedCandidateCount,
    expandToken: sourcingExpandToken,
    refreshToken: sourcingRefreshToken,
    defaultStatusFilter: pipelineCandidateStatusFilter,
  };

  const effectiveSourcingSessionId = panelSessionId || automationSessionId;
  const effectiveSourcingSearchId = panelSearchId || automationSearchId;

  const shouldShowCandidateSyncPanel =
    Boolean(effectiveSourcingSessionId) &&
    (
      (Array.isArray(postedChannels) && postedChannels.length > 0) ||
      Boolean(effectiveSourcingSearchId) ||
      Boolean(syncingPostedApplicants) ||
      Boolean(postedApplicantsLastSyncTime)
    );

  return (
    <aside
      className="flex-shrink-0 border-l border-gray-200 dark:border-gray-700 overflow-y-auto h-full"
      style={{
        background: isDark
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(20, 36, 50, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(59, 91, 219, 0.08) 0%, rgba(40, 172, 168, 0.08) 100%)',
        width: isStackedPanels
          ? '100%'
          : centerWidth !== null
          ? `calc(100% - ${centerWidth}px - 4px)`
          : '50%',
        minWidth: isStackedPanels ? '0px' : `${rightPanelMinWidthPx}px`,
        display: isStackedPanels && mobilePanelView !== 'results' ? 'none' : 'block',
        borderLeftWidth: isStackedPanels ? '0px' : '1px',
      }}
    >
      {!isMobileLayout && (
        <div
          className="sticky top-0 z-30 flex min-h-[58px] items-center justify-center border-b border-gray-200 px-4 py-2 dark:border-gray-700 sm:px-6"
          style={{
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <div className={`flex w-full items-center ${isStackedPanels ? 'justify-between gap-3' : 'justify-center'}`}>
            <ChatHeaderControls
              activeTab={renderedHeaderTab}
              onTabChange={onNavigateTab}
              onSearch={() => {}}
              disabledTabs={[]}
              visibleTabs={visibleTabs}
              fromDashboard={fromDashboard}
              variant="workspace"
            />
            {isStackedPanels && (
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => onMobilePanelViewChange?.('chat')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    mobilePanelView === 'chat'
                      ? 'text-white'
                      : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  style={mobilePanelView === 'chat' ? { backgroundColor: primaryColor } : {}}
                >
                  Chat
                </button>
                <button
                  type="button"
                  onClick={() => onMobilePanelViewChange?.('results')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    mobilePanelView === 'results'
                      ? 'text-white'
                      : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  style={mobilePanelView === 'results' ? { backgroundColor: primaryColor } : {}}
                >
                  Results
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="p-3 sm:p-4 xl:p-5" style={{ position: 'relative' }}>
        {rightPanelTransitioning && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDark ? 'rgba(17,24,39,0.45)' : 'rgba(255,255,255,0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 20,
            gap: '12px',
            borderRadius: '8px',
            transition: 'opacity 0.2s ease-in-out',
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: `3px solid ${primaryColor}30`,
              borderTopColor: primaryColor,
              borderRadius: '50%',
              animation: 'rpSpin 0.8s linear infinite',
            }} />
            <p style={{ color: '#6B7280', fontSize: '13px', fontWeight: 500 }}>Loading section…</p>
            <style>{`@keyframes rpSpin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {agentStageLabel && !jdText ? (
          <div>
            <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
              {agentStageLabel}
            </h2>
            <div className="space-y-4 mt-6">
              {[
                { label: 'Collecting Job Details', desc: 'Job title, location, experience, skills', done: false },
                { label: 'Review & Confirm', desc: 'Review collected details before generating', done: false },
                { label: 'Generate Job Description', desc: 'AI creates a professional JD', done: false },
                { label: 'Save & Next Steps', desc: 'Post job or start sourcing candidates', done: false },
              ].map((step, idx) => {
                const respType = lastResponseType || '';
                let stepStatus = 'pending';
                if (idx === 0 && (respType === 'jd_collecting' || respType === 'jd_confirm_fields' || respType === 'jd_preview' || respType === 'jd_saved')) stepStatus = respType === 'jd_collecting' ? 'active' : 'done';
                if (idx === 1 && (respType === 'jd_confirm_fields' || respType === 'jd_preview' || respType === 'jd_saved')) stepStatus = respType === 'jd_confirm_fields' ? 'active' : 'done';
                if (idx === 2 && (respType === 'jd_preview' || respType === 'jd_saved')) stepStatus = respType === 'jd_preview' ? 'active' : 'done';
                if (idx === 3 && respType === 'jd_saved') stepStatus = 'active';

                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-4 rounded-lg transition-all"
                    style={{
                      backgroundColor: stepStatus === 'active' ? `${primaryColor}08` : stepStatus === 'done' ? '#f0fdf4' : isDark ? '#1f2937' : '#f9fafb',
                      border: stepStatus === 'active' ? `1.5px solid ${primaryColor}30` : '1px solid transparent',
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                      style={{
                        backgroundColor: stepStatus === 'done' ? '#22c55e' : stepStatus === 'active' ? primaryColor : isDark ? '#374151' : '#e5e7eb',
                        color: stepStatus === 'done' || stepStatus === 'active' ? '#fff' : isDark ? '#9ca3af' : '#6b7280',
                      }}
                    >
                      {stepStatus === 'done' ? '✓' : idx + 1}
                    </div>
                    <div>
                      <div className={`text-sm font-medium ${stepStatus === 'active' ? '' : stepStatus === 'done' ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}
                        style={stepStatus === 'active' ? { color: primaryColor } : {}}
                      >
                        {step.label}
                        {stepStatus === 'active' && (
                          <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                            In Progress
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{step.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400" style={{ backgroundColor: isDark ? '#1f2937' : '#f3f4f6' }}>
              Provide job details in the chat on the left. You can give all details at once or answer one by one.
            </div>
          </div>
        ) : renderedHeaderTab === 'job_posting' && !canUseFeature('job_posting') ? (
          blockedPanel('job_posting')
        ) : renderedHeaderTab === 'job_posting' ? (
          <>
            {showJobPostingJdPanel ? (
              <>
                <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
                  Hiring Strategy Overview
                </h2>
                <div className="mt-2">
                  <JDOverview
                    jdText={jdText}
                    requirements={finalRequirements}
                    featureAccess={featureAccess}
                    onPostJD={canUseFeature('job_posting') && availablePostingChannels.length > 0 ? () => handleUserQueryForJD(getPostJobCommand()) : null}
                    onStartTalentSourcing={canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted ? () => handleUserQueryForJD('search candidates') : null}
                    showActionButtons={(canUseFeature('job_posting') && availablePostingChannels.length > 0) || (canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted)}
                  />
                </div>
              </>
            ) : showJobPostingPlanPanel ? (
              <>
                <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
                  JD Posting Plan
                </h2>
                <div className="mt-2">
                  {jobPostingPhase === 'collecting_details' ? (
                    <JobPostingDetails
                      selectedChannels={selectedChannels}
                      postingDetails={postingDetails}
                      onDetailsChange={onPostingDetailsChange}
                      onTriggerMessage={onPostingDetailsTriggerMessage}
                    />
                  ) : (
                    <JDPostingPlan
                      jdText={jdText}
                      requirements={finalRequirements}
                      selectedChannels={selectedChannels}
                      postingDetails={postingDetails}
                      onPostJob={onOpenJobPostingPreview}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="mt-2">
                <JobPostingLogs
                  sessionId={jobPosting?.panelSessionId || sourcing?.panelSessionId || sourcing?.automationSessionId || ''}
                  linkedinLogs={linkedinLogs}
                  naukriLogs={naukriLogs}
                  linkedinProcessing={linkedinProcessing}
                  naukriProcessing={naukriProcessing}
                  jobPostingActive={shouldForceJobPostingPanel}
                  selectedChannels={selectedChannels}
                  postedChannels={postedChannels}
                  onRequestPostJob={onRequestPostJob}
                  onRetryChannel={onRetryPostChannel}
                  availableChannels={availablePostingChannels}
                  onSyncApplicants={onSyncApplicants}
                  syncingApplicants={syncingPostedApplicants}
                  lastApplicantsSyncTime={postedApplicantsLastSyncTime}
                  expandToken={jobPostingExpandToken}
                />
              </div>
            )}
          </>
        ) : renderedHeaderTab === 'status' ? (
          <JobStatusPanel
            featureAccess={featureAccess}
            candidateCount={selectedJobCandidateCount}
            selectedJobCandidateCount={selectedJobCandidateCount}
            candidates={candidates.length === Number(selectedJobCandidateCount || 0) ? candidates : []}
            pipelineSnapshot={pipelineSnapshot}
            jobPostingPhase={jobPostingPhase}
            jobPostingProcessing={jobPostingProcessing || linkedinProcessing || naukriProcessing}
            jobPostingComplete={jobPostingComplete || Boolean(postedChannels.length > 0)}
            jobPostingFailed={jobPostingFailed}
            postedChannels={postedChannels}
            selectedChannels={selectedChannels}
            availablePostingChannels={availablePostingChannels}
            syncingPostedApplicants={syncingPostedApplicants}
            postedApplicantsLastSyncTime={postedApplicantsLastSyncTime}
            sourcingComplete={sourcingComplete}
            sourcingStats={sourcingStats}
            talentScoutingStarted={talentScoutingStarted}
            outreachPhase={outreachPhase}
            outreachCompleted={outreachCompleted}
            agentStageLabel={agentStageLabel}
            jdText={jdText}
            selectedJobHasPostedJob={selectedJobHasPostedJob}
            jobPostingLogs={jobPostingLogs}
            sourcingLogs={sourcingLogs}
            outreachLogs={outreachLogs}
            emailSyncState={emailSyncState}
            onRefreshStatus={onRefreshStatus}
            onNavigateTab={onNavigateTab}
            onOpenPipelineFilter={onOpenPipelineFilter}
            sessionId={jobPosting?.panelSessionId || panelSessionId || automationSessionId || ''}
            searchId={effectiveSourcingSearchId}
            interviewRefreshKey={interviewRefreshKey}
          />
        ) : renderedHeaderTab === 'sourcing' && !canUseFeature('sourcing') ? (
          blockedPanel('sourcing')
        ) : renderedHeaderTab === 'sourcing' ? (
          <>
            {(candidates && candidates.length > 0) || shouldShowCandidateSyncPanel ? (
              <div className="mt-2">
                <CandidateResults {...candidateResultsProps} />
              </div>
            ) : (
              <div className="mt-2">
                <TalentScoutingResults
                  googleLogs={googleSourcingLogs}
                  linkedinLogs={linkedinSourcingLogs}
                  resumeBoxLogs={resumeBoxSourcingLogs}
                  googleProcessing={googleSourcingProcessing}
                  linkedinProcessing={linkedinSourcingProcessing}
                  resumeBoxProcessing={resumeBoxSourcingProcessing}
                  googleCandidateCount={googleCandidateCount}
                  linkedinCandidateCount={linkedinCandidateCount}
                  resumeBoxCandidateCount={resumeBoxCandidateCount}
                  selectedSourcingChannels={selectedSourcingChannels}
                  googleSearchFoundCount={googleSearchFoundCount}
                  linkedinSearchFoundCount={linkedinSearchFoundCount}
                  expandToken={sourcingExpandToken}
                  sessionId={effectiveSourcingSessionId}
                />
              </div>
            )}
          </>
        ) : renderedHeaderTab === 'interview' && !canUseInterviews ? (
          blockedPanel('manual_interview')
        ) : renderedHeaderTab === 'interview' ? (
          <div className="mt-2">
            <InterviewWorkspace
              candidates={candidates}
              searchId={effectiveSourcingSearchId}
              sourceSessionId={effectiveSourcingSessionId}
              jobTitle={jobTitle}
              onStartInterviewCreation={onStartInterviewCreation}
              onOpenScorecard={onOpenScorecard}
              refreshKey={interviewRefreshKey}
              draftBlueprint={interviewSetupDraft}
              featureAccess={featureAccess}
            />
          </div>
        ) : talentScoutingStarted && !sourcingComplete && rightPanelMode === 'auto' && !shouldForceJobPostingPanel && !canUseFeature('sourcing') ? (
          blockedPanel('sourcing')
        ) : talentScoutingStarted && !sourcingComplete && rightPanelMode === 'auto' && !shouldForceJobPostingPanel ? (
          <>
            {(candidates && candidates.length > 0) || shouldShowCandidateSyncPanel ? (
              <div className="mt-2">
                <CandidateResults {...candidateResultsProps} />
              </div>
            ) : (
              <>
                <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
                  Identifying potential candidates across platforms
                </h2>
                <div className="mt-2">
                  <TalentScoutingResults
                    googleLogs={googleSourcingLogs}
                    linkedinLogs={linkedinSourcingLogs}
                    resumeBoxLogs={resumeBoxSourcingLogs}
                    googleProcessing={googleSourcingProcessing}
                    linkedinProcessing={linkedinSourcingProcessing}
                    resumeBoxProcessing={resumeBoxSourcingProcessing}
                    googleCandidateCount={googleCandidateCount}
                    linkedinCandidateCount={linkedinCandidateCount}
                    resumeBoxCandidateCount={resumeBoxCandidateCount}
                    selectedSourcingChannels={selectedSourcingChannels}
                    googleSearchFoundCount={googleSearchFoundCount}
                    linkedinSearchFoundCount={linkedinSearchFoundCount}
                    expandToken={sourcingExpandToken}
                    sessionId={effectiveSourcingSessionId}
                  />
                </div>
              </>
            )}
          </>
        ) : outreachPhase && outreachPhase !== 'idle' && rightPanelMode !== 'candidates' && rightPanelMode !== 'jd' && !canUseFeature('outreach') ? (
          blockedPanel('outreach')
        ) : outreachPhase && outreachPhase !== 'idle' && rightPanelMode !== 'candidates' && rightPanelMode !== 'jd' ? (
          (outreachPhase === 'sending' || outreachCompleted) && (outreachLogs.length > 0 || outreachSummary) ? (
            <OutreachLogs
              summary={outreachSummary}
              logs={outreachLogs}
              isSending={outreachSending && !outreachCompleted}
              isDark={isDark}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              totalCandidatesFallback={outreachCandidateIds.length}
            />
          ) : (
            <OutreachContent
              selectedCandidates={outreachCandidateIds}
              allCandidates={candidates}
              sessionId={automationSessionId}
              searchId={automationSearchId}
              jdDetails={finalRequirements}
              jdText={jdText}
              onBack={handleBackFromOutreach}
              onStartOutreach={handleSendOutreach}
              onRegenerateTemplate={() =>
                handleStartOutreach({
                  candidateIds: outreachCandidateIds,
                  candidates: outreachCandidates,
                  outreachChannels: selectedOutreachChannels,
                })
              }
              outreachTemplate={outreachTemplate}
              templatesByChannel={outreachTemplatesByChannel}
              isLoadingTemplate={isLoadingOutreachTemplate}
              isSendingOutreach={outreachPhase === 'sending'}
              companyName={outreachWorkspaceName || finalRequirements?.companyName || ''}
              selectedOutreachChannels={selectedOutreachChannels}
            />
          )
        ) : rightPanelMode === 'candidates' && (candidates.length > 0 || shouldShowCandidateSyncPanel) && !shouldForceJobPostingPanel && !canUseFeature('sourcing') ? (
          blockedPanel('sourcing')
        ) : rightPanelMode === 'candidates' && (candidates.length > 0 || shouldShowCandidateSyncPanel) && !shouldForceJobPostingPanel ? (
          <div className="mt-2">
            <CandidateResults {...candidateResultsProps} />
          </div>
        ) : rightPanelMode === 'jd' && jdText ? (
          <>
            <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
              Hiring Strategy Overview
            </h2>
            <div className="mt-2">
              <JDOverview
                jdText={jdText}
                requirements={finalRequirements}
                featureAccess={featureAccess}
                onPostJD={canUseFeature('job_posting') && availablePostingChannels.length > 0 ? () => handleUserQueryForJD(getPostJobCommand()) : null}
                onStartTalentSourcing={canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted ? () => handleUserQueryForJD('search candidates') : null}
                showActionButtons={(canUseFeature('job_posting') && availablePostingChannels.length > 0) || (canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted)}
              />
            </div>
          </>
        ) : (sourcingComplete || talentScoutingStarted || shouldShowCandidateSyncPanel) && (candidates.length > 0 || shouldShowCandidateSyncPanel) && !shouldForceJobPostingPanel && renderedHeaderTab !== 'job_posting' && !canUseFeature('sourcing') ? (
          blockedPanel('sourcing')
        ) : (sourcingComplete || talentScoutingStarted || shouldShowCandidateSyncPanel) && (candidates.length > 0 || shouldShowCandidateSyncPanel) && !shouldForceJobPostingPanel && renderedHeaderTab !== 'job_posting' ? (
          <div className="mt-2">
            <CandidateResults {...candidateResultsProps} />
          </div>
        ) : talentScoutingStarted && (!sourcingComplete || googleSourcingLogs.length > 0 || linkedinSourcingLogs.length > 0 || resumeBoxSourcingLogs.length > 0) && !shouldForceJobPostingPanel && renderedHeaderTab !== 'job_posting' && !canUseFeature('sourcing') ? (
          blockedPanel('sourcing')
        ) : talentScoutingStarted && (!sourcingComplete || googleSourcingLogs.length > 0 || linkedinSourcingLogs.length > 0 || resumeBoxSourcingLogs.length > 0) && !shouldForceJobPostingPanel && renderedHeaderTab !== 'job_posting' ? (
          <>
            <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
              Identifying potential candidates across platforms
            </h2>
            <div className="mt-2">
              <TalentScoutingResults
                requirements={finalRequirements}
                jdText={jdText}
                googleLogs={googleSourcingLogs}
                linkedinLogs={linkedinSourcingLogs}
                resumeBoxLogs={resumeBoxSourcingLogs}
                googleProcessing={googleSourcingProcessing}
                linkedinProcessing={linkedinSourcingProcessing}
                resumeBoxProcessing={resumeBoxSourcingProcessing}
                googleCandidateCount={googleCandidateCount}
                linkedinCandidateCount={linkedinCandidateCount}
                resumeBoxCandidateCount={resumeBoxCandidateCount}
                selectedSourcingChannels={selectedSourcingChannels}
                sourcingStats={sourcingStats}
                enrichedCandidateCount={enrichedCandidateCount}
                googleSearchFoundCount={googleSearchFoundCount}
                linkedinSearchFoundCount={linkedinSearchFoundCount}
                matchedCandidateCount={matchedCandidateCount}
                sessionId={effectiveSourcingSessionId}
              />
            </div>
          </>
        ) : ((!showResultsPanel && analysisUxPhase !== 'done') || analysisUxPhase === 'building') && (
          analysisUxPhase === 'analysing' ||
          analysisUxPhase === 'analysed' ||
          analysisUxPhase === 'building' ||
          chatApiLoading ||
          lifecycleLoading ||
          isJDLoading
        ) ? (
          <>
            <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
              {analysisUxPhase === 'analysing' ? 'Analyzing Your Request' : analysisUxPhase === 'analysed' ? 'Review Your Requirements' : 'Building your sourcing strategy'}
            </h2>
            <div className="space-y-6">
              <ProgressCard
                title="Analyzing Your Requirements and extracting Key Criteria."
                icon={LightbulbIcon}
                variant={
                  analysisUxPhase === 'idle'
                    ? 'idle'
                    : analysisUxPhase === 'analysing'
                    ? 'active'
                    : analysisUxPhase === 'analysed' || analysisUxPhase === 'building' || analysisUxPhase === 'done'
                    ? 'completed'
                    : 'idle'
                }
              />
              <ProgressCard
                title="Understand the extracted Key Criteria and Building you Sourcing Strategy."
                icon={BarChartIcon}
                variant={
                  analysisUxPhase === 'idle'
                    ? 'idle'
                    : analysisUxPhase === 'analysing'
                    ? 'inactive'
                    : analysisUxPhase === 'analysed' || analysisUxPhase === 'building'
                    ? 'active'
                    : analysisUxPhase === 'done'
                    ? 'completed'
                    : 'idle'
                }
              />
            </div>
            <div className="mt-8 space-y-3">
              {analysisUxPhase !== 'done' && analysis.jdError && !isJDLoading ? (
                <div className="text-sm text-red-600 dark:text-red-400">
                  {analysis.jdError}
                </div>
              ) : null}
              {jdText && !isJDLoading ? (
                <ChatCard
                  variant="primary"
                  size="md"
                  rounded="lg"
                  className="chat_mt_sm max-h-[360px] overflow-y-auto"
                  style={{
                    backgroundColor: isDark ? 'rgba(17, 24, 39, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    borderColor: isDark ? 'rgba(75, 85, 99, 1)' : 'rgba(229, 231, 235, 1)',
                  }}
                >
                  <h3 className="chat_heading_sm chat_font_semibold chat_mb_md" style={{ color: primaryColor }}>
                    Generated Job Description
                  </h3>
                  <pre className="whitespace-pre-wrap chat_text_xs chat_text_primary">
                    {jdText}
                  </pre>
                </ChatCard>
              ) : null}
            </div>
          </>
        ) : analysisUxPhase === 'done' ? (
          <>
            <h2 className="chat_heading_md chat_mb_lg text-center" style={{ color: primaryColor }}>
              Hiring Strategy Overview
            </h2>
            <div className="mt-2">
              <JDOverview
                jdText={jdText}
                requirements={finalRequirements}
                featureAccess={featureAccess}
                onPostJD={canUseFeature('job_posting') && availablePostingChannels.length > 0 ? () => handleUserQueryForJD(getPostJobCommand()) : null}
                onStartTalentSourcing={canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted ? () => handleUserQueryForJD('search candidates') : null}
                showActionButtons={(canUseFeature('job_posting') && availablePostingChannels.length > 0) || (canUseFeature('sourcing') && !sourcingComplete && !talentScoutingStarted)}
              />
            </div>
          </>
        ) : (
          <div className="min-h-[220px]" />
        )}
      </div>
    </aside>
  );
};

export default ChatRightPanel;
